package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.modules.account.data.source.ConfirmDeletionDataSource
import org.jetbrains.compose.web.css.Style
import com.appsyde.shared.modules.account.data.source.ConfirmDeletionResultDto
import com.appsyde.shared.modules.account.data.source.ConfirmDeletionStatusDto
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesCards
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.koin.core.context.GlobalContext
import org.w3c.dom.HTMLInputElement
import kotlinx.browser.window

private const val CONFIRM_TYPED_TEXT = "SUPPRIMER"

@Composable
fun ConfirmAccountDeletionPage() {
    Style(AppStylesGlobal)
    Style(AppStylesAccount)
    Style(AppStylesCards)

    val token = remember { getConfirmDeletionTokenFromUrl() }
    val dataSource = remember { GlobalContext.get().get<ConfirmDeletionDataSource>() }

    var status by remember { mutableStateOf<ConfirmDeletionStatusDto?>(null) }
    var postResult by remember { mutableStateOf<ConfirmDeletionResultDto?>(null) }
    var typedText by remember { mutableStateOf("") }
    var isSubmitting by remember { mutableStateOf(false) }
    var submitTrigger by remember { mutableStateOf(0) }
    var inputFocused by remember { mutableStateOf(false) }

    LaunchedEffect(token) {
        if (token != null) status = dataSource.getStatus(token)
        else status = ConfirmDeletionStatusDto.Invalid
    }

    LaunchedEffect(submitTrigger) {
        if (submitTrigger > 0 && token != null) {
            isSubmitting = true
            postResult = dataSource.executeDeletion(token)
            isSubmitting = false
        }
    }

    Div(attrs = { classes(AppStylesAccount.confirmDeletionPageWrap) }) {
        Div(attrs = { classes(AppStylesAccount.confirmDeletionCard) }) {
            when (val s = status) {
                null -> {
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Chargement…") }
                }
                is ConfirmDeletionStatusDto.Invalid -> {
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Lien invalide") }
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionBody) }) {
                        Text("Ce lien de confirmation est invalide ou manquant.")
                    }
                }
                is ConfirmDeletionStatusDto.Expired -> {
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Lien expiré") }
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionBody) }) {
                        Text("Cette demande de suppression a expiré. Veuillez initier une nouvelle demande depuis les paramètres.")
                    }
                }
                is ConfirmDeletionStatusDto.AlreadyUsed -> {
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Demande déjà traitée") }
                    Div(attrs = { classes(AppStylesAccount.confirmDeletionBody) }) {
                        Text("Cette demande de suppression a déjà été confirmée.")
                    }
                }
                is ConfirmDeletionStatusDto.Valid -> when (val result = postResult) {
                    null -> {
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) {
                            Text("Confirmer la suppression de votre compte")
                        }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionSubtext) }) {
                            Text("Cette action est irréversible.")
                        }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionSection, AppStylesAccount.confirmDeletionAccountBlock) }) {
                            Div(attrs = { classes(AppStylesAccount.confirmDeletionAccountLabel) }) { Text("Compte concerné") }
                            Div(attrs = { classes(AppStylesAccount.confirmDeletionAccountEmail) }) { Text(s.accountPrimaryEmail) }
                        }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionSection) }) {
                            Div(attrs = { classes(AppStylesAccount.deleteAccountModalSubheading) }) { Text("La suppression supprimera :") }
                            Ul(attrs = { classes(AppStylesAccount.confirmDeletionList) }) {
                                Li(attrs = { classes(AppStylesAccount.confirmDeletionListItem) }) { Text("Tickets et messages enregistrés dans AppSyde") }
                                Li(attrs = { classes(AppStylesAccount.confirmDeletionListItem) }) { Text("Paramètres (ton IA, signature, configuration)") }
                                Li(attrs = { classes(AppStylesAccount.confirmDeletionListItem) }) { Text("L'accès à votre espace AppSyde") }
                            }
                        }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionSection) }) {
                            Div(attrs = { classes(AppStylesAccount.deleteAccountModalSubheading, AppStylesAccount.confirmDeletionConfirmLabel) }) {
                                Text("Pour confirmer, tapez ")
                                Span(attrs = { classes(AppStylesAccount.confirmDeletionBold) }) { Text(CONFIRM_TYPED_TEXT) }
                            }
                            Input(InputType.Text, attrs = {
                                classes(AppStylesAccount.confirmDeletionInput, *(if (inputFocused) arrayOf(AppStylesAccount.confirmDeletionInputFocus) else emptyArray()))
                                attr("value", typedText)
                                onInput { typedText = (it.target as? HTMLInputElement)?.value ?: "" }
                                onFocus { inputFocused = true }
                                onBlur { inputFocused = false }
                            })
                        }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionButtons) }) {
                            Button(attrs = {
                                classes(AppStylesAccount.confirmDeletionButtonSecondary)
                                attr("type", "button")
                                onClick { window.history.back() }
                            }) {
                                Text("Annuler")
                            }
                            Button(attrs = {
                                classes(AppStylesAccount.deleteAccountModalButtonPrimary)
                                attr("type", "button")
                                if (isSubmitting || typedText != CONFIRM_TYPED_TEXT) attr("disabled", "true")
                                onClick {
                                    if (token != null && typedText == CONFIRM_TYPED_TEXT && !isSubmitting)
                                        submitTrigger++
                                }
                            }) {
                                Text(if (isSubmitting) "Suppression…" else "Supprimer définitivement")
                            }
                        }
                    }
                    is ConfirmDeletionResultDto.Deleted -> {
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Compte supprimé") }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionBody) }) {
                            Text("Votre compte a été supprimé définitivement.")
                        }
                    }
                    else -> {
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionTitle) }) { Text("Erreur") }
                        Div(attrs = { classes(AppStylesAccount.confirmDeletionBody) }) {
                            Text(if (result is ConfirmDeletionResultDto.AlreadyUsed) "Cette demande a déjà été traitée." else "Lien invalide ou expiré.")
                        }
                    }
                }
            }
        }
    }
}

private fun getConfirmDeletionTokenFromUrl(): String? {
    val search = window.location.search ?: return null
    if (!search.startsWith("?")) return null
    return search.removePrefix("?").split("&").mapNotNull { param ->
        val parts = param.split("=", limit = 2)
        if (parts.getOrNull(0) == "token") parts.getOrNull(1) else null
    }.firstOrNull()?.let { decoded ->
        try { js("decodeURIComponent").invoke(decoded) as String } catch (_: Throwable) { decoded }
    }?.takeIf { it.isNotBlank() }
}
